<script lang="ts" setup>
import { sleep } from '@antfu/utils'

const props = defineProps<{
  active?: boolean
  placeholder?: string
  color?: string
  activeColor?: string
  closeOnBlur?: boolean
}>()

const emit = defineEmits<{
  (e: 'search', value: string): void
  (e: 'update:active', value: boolean): void
  (e: 'submit'): void
}>()

const [isSearchActive, toggleSearch] = useToggle()
const searchContainer = ref(null)
const searchInput = ref<HTMLInputElement>()
const search = ref('')
watchThrottled(search, v => emit('search', v), { throttle: 250 })

function closeInput() {
  toggleSearch(false)
  emit('update:active', false)
  search.value = ''
}

async function openInput() {
  toggleSearch(true)
  emit('update:active', true)
  search.value = ''
  await nextTick()
  searchInput.value?.focus()
}

function updateInput(event: any) {
  const inputValue = event.target.value
  event.target.composing = false
  search.value = inputValue
}

onClickOutside(searchContainer, async () => {
  if (props.closeOnBlur || !search.value) {
    await sleep(100) // don't overhead the UI refresh right now, first let the navigation happens
    closeInput()
  }
})

whenever(() => !props.active, closeInput)

defineExpose({
  reset() { search.value = '' },
  closeInput,
})

/**
 * Clear and close search input when switching community
 */
tryOnBeforeUnmount(useEventBus('feed-search')
  .on((event) => {
    if (event === 'clear-search')
      closeInput()
  }))
</script>

<template>
  <div
    ref="searchContainer"
    class="search-container relative flex rounded-full w-full h-full items-center
      justify-end min-w-[38px] transition-all duration-500"
  >
    <transition name="scale-x">
      <input
        v-show="isSearchActive"
        ref="searchInput"
        autocomplete="off"
        autofocus
        class="border-grey-5 rounded-full overflow-hidden text-ellipsis transition-all
          form-control form-control-root pr-9 font-[arial]"
        :placeholder="placeholder"
        :value="search"
        @input="updateInput"
        @keyup.enter="emit('submit')"
        @keyup.escape="closeInput"
      >
    </transition>
    <transition-group name="fade">
      <vu-icon-btn
        v-if="!isSearchActive"
        v-tooltip.bottom="placeholder"
        class="!absolute input-search_dynamic-icon text-2xl"
        :color="color"
        icon="search"
        @click="openInput"
      />
      <vu-icon-btn
        v-else-if="search"
        v-tooltip.bottom="'Clear'"
        class="!absolute input-search_dynamic-icon !text-lg"
        icon="clear"
        @click="openInput"
      />
    </transition-group>
  </div>
</template>

<style scoped lang="scss">
.input-search_dynamic-icon {
  display: flex !important;
  @apply items-center justify-center;
}
</style>
